import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CycleCalendar from "@/pages/CycleCalendar";
import MoonCycles from "@/pages/MoonCycles";
import Remedies from "@/pages/Remedies";
import Astrology from "@/pages/Astrology";
import Rituals from "@/pages/Rituals";
import AITips from "@/pages/AITips";
import Settings from "@/pages/Settings";
import Predictions from "@/pages/Predictions";
import Analytics from "@/pages/Analytics";
import Journal from "@/pages/Journal";
import WombHealing from "@/pages/WombHealing";
import DailyHoroscope from "@/pages/DailyHoroscope";
import Forum from "@/pages/Forum";
import Transits from "@/pages/Transits";
import Synastry from "@/pages/Synastry";
import Achievements from "@/pages/Achievements";
import Numerology from "@/pages/Numerology";
import AdminPanel from "@/pages/AdminPanel";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminSystemHealthPage from "@/pages/AdminSystemHealthPage";
import SymptomTrends from "@/pages/SymptomTrends";
import HealthDataHistory from "@/pages/HealthDataHistory";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PartnerView from "@/pages/PartnerView";
import PregnancyExplorer from "@/pages/PregnancyExplorer";
import PregnancyIssueHelper from "@/pages/PregnancyIssueHelper";
import TTCDashboard from "@/pages/TTCDashboard";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [subscription, setSubscription] = useState(null); // null=unknown, {is_premium, tier, status, ...}
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      setToken(null);
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch subscription status whenever the user logs in / out.
  // Single source of truth for premium gating across the app.
  const refreshSubscription = useCallback(async () => {
    if (!token || !user) {
      setSubscription(null);
      return null;
    }
    setSubscriptionLoading(true);
    try {
      const res = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setSubscription(res.data);
      return res.data;
    } catch (error) {
      setSubscription({ is_premium: false, tier: "free", status: "unknown" });
      return null;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data.user);
    setToken(response.data.token);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name }, { withCredentials: true });
    setUser(response.data.user);
    setToken(response.data.token);
    localStorage.setItem("token", response.data.token);
    return response.data;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      // error handled silently
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
  };

  const setAuthUser = (userData) => {
    setUser(userData);
  };

  const loginWithApple = async (authResponse) => {
    try {
      const response = await axios.post(`${API}/auth/apple/callback`, {
        authorization_code: authResponse.authorization.code,
        user: authResponse.user || null,
      }, { withCredentials: true });
      setSession(response.data.user, response.data.session_token);
      return response.data;
    } catch (error) {
      // error handled silently
      throw error;
    }
  };

  const setSession = (userData, sessionToken) => {
    setUser(userData);
    setToken(sessionToken);
    if (sessionToken) {
      localStorage.setItem("token", sessionToken);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, loginWithApple, token, setAuthUser, setSession, checkAuth, subscription, subscriptionLoading, refreshSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        navigate('/login', { replace: true });
        return;
      }

      // Clear the hash IMMEDIATELY to prevent re-processing (session_id is single-use)
      window.history.replaceState(null, '', window.location.pathname);

      try {
        const response = await axios.post(
          `${API}/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        const userData = response.data.user;
        const sessionToken = response.data.session_token;
        setSession(userData, sessionToken);
        toast.success("Welcome to Sacred Celestial Flow!");
        navigate('/dashboard', { replace: true });
      } catch (error) {
        const detail = error.response?.data?.detail || "Please try signing in again.";
        toast.error(`Authentication failed: ${detail}`);
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setSession]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-mystic-900 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-12 h-12 border-4 border-mystic-300 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-mystic-100">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-mystic-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-mystic-300 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-mystic-100">Loading...</p>
        </div>
      </div>
    );
  }

  // If user data passed from AuthCallback, skip redirect
  if (user) {
    return children;
  }

  return <Navigate to="/login" replace />;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/partner/:token" element={<PartnerView />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout><CycleCalendar /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/moon" element={
        <ProtectedRoute>
          <Layout><MoonCycles /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/remedies" element={
        <ProtectedRoute>
          <Layout><Remedies /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/astrology" element={
        <ProtectedRoute>
          <Layout><Astrology /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/rituals" element={
        <ProtectedRoute>
          <Layout><Rituals /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ai-tips" element={
        <ProtectedRoute>
          <Layout><AITips /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/predictions" element={
        <ProtectedRoute>
          <Layout><Predictions /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pregnancy/explore" element={
        <ProtectedRoute>
          <Layout><PregnancyExplorer /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/pregnancy/issues" element={
        <ProtectedRoute>
          <Layout><PregnancyIssueHelper /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ttc/dashboard" element={
        <ProtectedRoute>
          <Layout><TTCDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <Layout><Analytics /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/journal" element={
        <ProtectedRoute>
          <Layout><Journal /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/womb-healing" element={
        <ProtectedRoute>
          <Layout><WombHealing /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/daily-horoscope" element={
        <ProtectedRoute>
          <Layout><DailyHoroscope /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/forum" element={
        <ProtectedRoute>
          <Layout><Forum /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/transits" element={
        <ProtectedRoute>
          <Layout><Transits /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/synastry" element={
        <ProtectedRoute>
          <Layout><Synastry /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/achievements" element={
        <ProtectedRoute>
          <Layout><Achievements /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/numerology" element={
        <ProtectedRoute>
          <Layout><Numerology /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/symptom-trends" element={
        <ProtectedRoute>
          <Layout><SymptomTrends /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/health-data" element={
        <ProtectedRoute>
          <Layout><HealthDataHistory /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout><AdminPanel /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/dashboard" element={
        <ProtectedRoute>
          <Layout><AdminDashboardPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/system" element={
        <ProtectedRoute>
          <Layout><AdminSystemHealthPage /></Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-mystic-900">
          <div className="stars"></div>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };
